import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatch,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class TextInputPropsBuilder extends PropsBuilder<'textInput'> {
  override setPlaceholder = (placeholder: string | undefined): this => {
    this.patchProps({ content: { input: { placeholder } } });
    return this;
  };

  override setMargin = (margin: string | undefined): this => {
    this.patchProps({ style: { general: { offset: { margin } } } });
    return this;
  };

  override setParentContentProps = (
    elementProps: PartialPropsOf<'textInput'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'textInput'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatch());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(
        overrides.label?.align,
        'content.title.align.horizontal',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { align: { horizontal: overrides.label.align } } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'textInput'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'textInput'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: { title: undefined, accent: undefined },
          inputFont: undefined,
          input: undefined,
        },
      });
      return this;
    }

    const { titleLabel, fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (
      shouldPatchProp(
        fieldLabel?.fontFamily,
        'style.inputFont.fontFamily',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ style: { inputFont: { fontFamily: fieldLabel.fontFamily } } });
    }
    if (
      shouldPatchProp(fieldLabel?.fontSize, 'style.inputFont.fontSize', elementProps, initialValues)
    ) {
      this.patchProps({ style: { inputFont: { fontSize: fieldLabel.fontSize } } });
    }
    if (shouldPatchProp(fieldLabel?.style, 'style.inputFont.style', elementProps, initialValues)) {
      this.patchProps({ style: { inputFont: { style: fieldLabel.style } } });
    }
    if (shouldPatchProp(fieldLabel?.color, 'style.input.value', elementProps, initialValues)) {
      this.patchProps({ style: { input: { value: fieldLabel.color } } });
    }

    if (shouldPatchProp(fieldBox?.accent, 'style.general.accent', elementProps, initialValues)) {
      this.patchProps({ style: { general: { accent: fieldBox.accent } } });
    }
    if (shouldPatchProp(fieldBox?.fill, 'style.input.fill', elementProps, initialValues)) {
      this.patchProps({ style: { input: { fill: fieldBox.fill } } });
    }
    if (shouldPatchProp(fieldBox?.outline, 'style.input.border', elementProps, initialValues)) {
      this.patchProps({ style: { input: { border: fieldBox.outline } } });
    }
    if (shouldPatchProp(fieldBox?.radius, 'style.input.radius', elementProps, initialValues)) {
      this.patchProps({ style: { input: { radius: fieldBox.radius } } });
    }

    return this;
  };

  override setReadonly = (readonly: boolean): this => {
    this.patchProps({
      content: {
        input: {
          readonly,
        },
      },
      style: {
        input: {
          border: { selectorType: readonly ? 'none' : 'solid' },
          fill: { color1: readonly ? 'transparent' : '$t.fillInternal' },
        },
      },
    });
    return this;
  };

  constructor(props: PartialPropsOf<'textInput'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
